import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronLeft, ChevronRight, Search, Trash2, ShieldOff, ArrowUpDown, Loader2, FlaskConical, Download, Route, CloudSun, MoreHorizontal, Check, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserPathsDialog } from "@/components/users/UserPathsDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { PageHelpButton } from "@/components/PageHelpButton";

interface UserRow {
  user_id: string;
  email: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
  onboarding_completed: boolean;
  is_tester: boolean;
  birth_date: string | null;
  has_responses: boolean;
  has_meteo: boolean;
  data_visible: boolean;
  export_block_reason: "legal" | "underage" | "both" | null;
  legal_accepted_version: string | null;
}

function userHasData(u: UserRow): boolean {
  return u.has_responses || u.has_meteo;
}

function userDataTooltip(u: UserRow): string {
  if (!userHasData(u)) return "Aucune donnée en base";
  const parts: string[] = [];
  if (u.has_responses) parts.push("réponses interactives");
  if (u.has_meteo) parts.push("météo");
  let msg = `${parts.join(" et ")} en base`;
  if (!u.data_visible) msg += " · Exclu des exports";
  return msg.charAt(0).toUpperCase() + msg.slice(1);
}

function exportBlockBadge(u: UserRow): { label: string; title: string } | null {
  if (u.data_visible) return null;
  switch (u.export_block_reason) {
    case "legal":
      return {
        label: "CGU non acceptées",
        title: u.legal_accepted_version
          ? `Version acceptée : v${u.legal_accepted_version} · Données exclues des exports`
          : "CGU/PC non acceptées à la version courante · Données exclues des exports",
      };
    case "underage":
      return { label: "< 18 ans", title: "Utilisateur de moins de 18 ans · Données exclues des exports" };
    case "both":
      return { label: "Non exportable", title: "< 18 ans et CGU/PC non acceptées · Données exclues des exports" };
    default:
      return { label: "Non éligible export", title: "Données exclues des exports" };
  }
}

export default function Users() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(() => Number(localStorage.getItem("users-page-size")) || 10);
  const setPageSize = (v: number) => { setPageSizeState(v); localStorage.setItem("users-page-size", String(v)); };
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState<"created_at" | "last_sign_in">("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const [deleteDialog, setDeleteDialog] = useState<UserRow | null>(null);
  const [anonymizeDialog, setAnonymizeDialog] = useState<UserRow | null>(null);
  const [pathsDialog, setPathsDialog] = useState<UserRow | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMeteoLoading, setExportMeteoLoading] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkAnonymizeDialog, setBulkAnonymizeDialog] = useState(false);
  const [bulkTesterDialog, setBulkTesterDialog] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const allSelected = users.length > 0 && users.every((u) => selectedIds.has(u.user_id));
  const someSelected = selectedIds.size > 0;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((u) => u.user_id)));
    }
  };

  // Clear selection on page/search change
  useEffect(() => { setSelectedIds(new Set()); }, [page, pageSize, search, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const params = new URLSearchParams({
        action: "list",
        page: String(page),
        pageSize: String(pageSize),
        sortBy,
        sortDir,
      });
      if (search) params.set("search", search);

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users?${params}`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      toast.error(e.message || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, sortBy, sortDir]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const toggleSort = (col: "created_at" | "last_sign_in") => {
    if (sortBy === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  };

  const callAction = async (action: string, userId: string, extra?: Record<string, any>) => {
    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users?action=${action}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ user_id: userId, ...extra }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      return true;
    } catch (e: any) {
      toast.error(e.message || "Erreur");
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    const ok = await callAction("delete", deleteDialog.user_id);
    if (ok) {
      toast.success("Utilisateur supprimé");
      setDeleteDialog(null);
      fetchUsers();
    }
  };

  const handleAnonymize = async () => {
    if (!anonymizeDialog) return;
    const ok = await callAction("anonymize", anonymizeDialog.user_id);
    if (ok) {
      toast.success("Données anonymisées");
      setAnonymizeDialog(null);
      fetchUsers();
    }
  };

  const handleToggleTester = async (user: UserRow) => {
    const newValue = !user.is_tester;
    const ok = await callAction("toggle_tester", user.user_id, { is_tester: newValue });
    if (ok) {
      toast.success(newValue ? "Marqué comme testeur" : "Retiré des testeurs");
      setUsers(prev => prev.map(u =>
        u.user_id === user.user_id ? { ...u, is_tester: newValue } : u
      ));
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-responses`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Erreur export");
      }
      const participants = res.headers.get("X-Export-Participants");
      const rows = res.headers.get("X-Export-Rows");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "export-responses.csv";
      a.click();
      URL.revokeObjectURL(url);
      if (participants != null && rows != null) {
        const p = Number(participants);
        const r = Number(rows);
        toast.success(
          `Export téléchargé · ${p} participant${p > 1 ? "s" : ""}, ${r} ligne${r > 1 ? "s" : ""}`,
        );
      } else {
        toast.success("Export téléchargé");
      }
    } catch (e: any) {
      toast.error(e.message || "Erreur lors de l'export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportMeteo = async () => {
    setExportMeteoLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-meteo`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Erreur export météo");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "export-meteo.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export météo téléchargé");
    } catch (e: any) {
      toast.error(e.message || "Erreur lors de l'export météo");
    } finally {
      setExportMeteoLoading(false);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    return format(new Date(d), "dd MMM yyyy HH:mm", { locale: fr });
  };

  const handleBulkAction = async (action: string, extra?: Record<string, any>) => {
    setBulkActionLoading(true);
    let successCount = 0;
    const ids = Array.from(selectedIds);
    for (const userId of ids) {
      const ok = await callAction(action, userId, extra);
      if (ok) successCount++;
    }
    setBulkActionLoading(false);
    return successCount;
  };

  const handleBulkDelete = async () => {
    const count = await handleBulkAction("delete");
    toast.success(`${count} utilisateur(s) supprimé(s)`);
    setBulkDeleteDialog(false);
    setSelectedIds(new Set());
    fetchUsers();
  };

  const handleBulkAnonymize = async () => {
    const count = await handleBulkAction("anonymize");
    toast.success(`${count} utilisateur(s) anonymisé(s)`);
    setBulkAnonymizeDialog(false);
    setSelectedIds(new Set());
    fetchUsers();
  };

  const handleBulkTester = async () => {
    const count = await handleBulkAction("toggle_tester", { is_tester: true });
    toast.success(`${count} utilisateur(s) marqué(s) comme testeur`);
    setBulkTesterDialog(false);
    setSelectedIds(new Set());
    fetchUsers();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" data-tour="users-title" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Utilisateurs
        </h1>
        <PageHelpButton tourKey="users" />
      </div>

      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Liste des utilisateurs
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportCSV()}
              disabled={exportLoading}
              data-tour="users-export"
            >
              <Download className="h-4 w-4 mr-1" />
              {exportLoading ? "Export…" : "Exporter CSV"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={exportMeteoLoading}
                  title="Autres exports"
                >
                  {exportMeteoLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MoreHorizontal className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExportMeteo()} disabled={exportMeteoLoading}>
                  <CloudSun className="h-4 w-4 mr-2" />
                  Exporter météo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Input
              placeholder="Rechercher nom, prénom…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="w-64"
              data-tour="users-search"
            />
            <Button size="icon" variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Aucun utilisateur trouvé.</p>
          ) : (
            <>
              {/* Bulk action bar */}
              {someSelected && (
                <div className="flex items-center gap-2 mb-3 p-2 rounded-md bg-muted/50 border border-border">
                  <span className="text-sm font-medium text-muted-foreground">
                    {selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}
                  </span>
                  <div className="flex items-center gap-1 ml-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBulkTesterDialog(true)}
                      disabled={bulkActionLoading}
                    >
                      <FlaskConical className="h-3.5 w-3.5 mr-1" />
                      Testeurs
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBulkAnonymizeDialog(true)}
                      disabled={bulkActionLoading}
                    >
                      <ShieldOff className="h-3.5 w-3.5 mr-1" />
                      Anonymiser
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBulkDeleteDialog(true)}
                      disabled={bulkActionLoading}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              )}

              <TooltipProvider delayDuration={300}>
              <Table data-tour="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>
                      <button
                        onClick={() => toggleSort("created_at")}
                        className="flex items-center gap-1 hover:text-foreground transition-colors"
                      >
                        Inscription
                        <ArrowUpDown className={`h-3 w-3 ${sortBy === "created_at" ? "text-foreground" : "text-muted-foreground"}`} />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        onClick={() => toggleSort("last_sign_in")}
                        className="flex items-center gap-1 hover:text-foreground transition-colors"
                      >
                        Dernier accès
                        <ArrowUpDown className={`h-3 w-3 ${sortBy === "last_sign_in" ? "text-foreground" : "text-muted-foreground"}`} />
                      </button>
                    </TableHead>
                    <TableHead className="w-16 text-center" title="Réponses interactives et/ou météo en base">
                      Données
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const hasData = userHasData(u);
                    const exportBadge = exportBlockBadge(u);
                    return (
                      <TableRow
                        key={u.user_id}
                        className={u.is_tester ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}
                      >
                        <TableCell className="w-10">
                          <Checkbox
                            checked={selectedIds.has(u.user_id)}
                            onCheckedChange={() => toggleSelect(u.user_id)}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-2 flex-wrap">
                            {u.email || "—"}
                            {u.is_tester && (
                              <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 dark:text-amber-400 gap-1">
                                <FlaskConical className="h-3 w-3" />
                                Testeur
                              </Badge>
                            )}
                            {exportBadge && (
                              <Badge
                                variant="outline"
                                className="text-xs text-muted-foreground"
                                title={exportBadge.title}
                              >
                                {exportBadge.label}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.created_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.last_sign_in_at)}
                        </TableCell>
                        <TableCell className="text-sm text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex justify-center" tabIndex={0}>
                                {hasData ? (
                                  <Check
                                    className={`h-4 w-4 ${u.data_visible ? "text-green-600 dark:text-green-500" : "text-muted-foreground"}`}
                                    aria-label={userDataTooltip(u)}
                                  />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground/60" aria-label="Aucune donnée" />
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{userDataTooltip(u)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              title={u.onboarding_completed ? "Parcours de l'utilisateur" : "Onboarding non terminé"}
                              onClick={() => setPathsDialog(u)}
                              disabled={!u.onboarding_completed}
                            >
                              <Route className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title={u.is_tester ? "Retirer le statut testeur" : "Marquer comme testeur"}
                              onClick={() => handleToggleTester(u)}
                              disabled={actionLoading}
                              className={u.is_tester ? "text-amber-600 hover:text-amber-700" : ""}
                            >
                              <FlaskConical className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Anonymiser (RGPD)"
                              onClick={() => setAnonymizeDialog(u)}
                            >
                              <ShieldOff className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              title="Supprimer"
                              onClick={() => setDeleteDialog(u)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TooltipProvider>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Lignes par page :</span>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 20, 50, 100].map((s) => (
                        <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span>{total} résultat{total > 1 ? "s" : ""}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2">{page} / {totalPages}</span>
                  <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'utilisateur <strong>{deleteDialog?.email || deleteDialog?.user_id.slice(0, 8)}</strong> et toutes ses données seront définitivement supprimés. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Anonymize Confirmation */}
      <AlertDialog open={!!anonymizeDialog} onOpenChange={(o) => !o && setAnonymizeDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anonymiser cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les données personnelles de <strong>{anonymizeDialog?.email || anonymizeDialog?.user_id.slice(0, 8)}</strong> (nom, prénom, email) seront remplacées par des valeurs anonymes. Le compte restera actif mais ne sera plus identifiable. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAnonymize}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? "Anonymisation…" : "Anonymiser"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialog} onOpenChange={(o) => !o && setBulkDeleteDialog(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedIds.size} utilisateur{selectedIds.size > 1 ? "s" : ""} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Tous les utilisateurs sélectionnés et leurs données seront définitivement supprimés. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkActionLoading ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Anonymize Confirmation */}
      <AlertDialog open={bulkAnonymizeDialog} onOpenChange={(o) => !o && setBulkAnonymizeDialog(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anonymiser {selectedIds.size} utilisateur{selectedIds.size > 1 ? "s" : ""} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les données personnelles des utilisateurs sélectionnés seront remplacées par des valeurs anonymes. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkAnonymize}
              disabled={bulkActionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkActionLoading ? "Anonymisation…" : "Anonymiser"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Tester Confirmation */}
      <AlertDialog open={bulkTesterDialog} onOpenChange={(o) => !o && setBulkTesterDialog(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marquer {selectedIds.size} utilisateur{selectedIds.size > 1 ? "s" : ""} comme testeur{selectedIds.size > 1 ? "s" : ""} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les utilisateurs sélectionnés seront marqués comme testeurs et exclus des statistiques du dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkTester}
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? "En cours…" : "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Paths Dialog */}
      <UserPathsDialog
        open={!!pathsDialog}
        onOpenChange={(o) => !o && setPathsDialog(null)}
        user={pathsDialog}
      />
    </div>
  );
}
